import { useQuery } from '@apollo/client'
import { differenceInCalendarDays } from 'date-fns'
import { Symptom } from '../../forms/symptomsForm'
import {
  QuestionnaireSurveys,
  QuestionnaireSurveysVariables,
  SurveyType,
  SortDirectionEnum,
} from '../../types'
import {
  QuestionnaireQuestionKeys as QuestionKeys,
  QuestionnaireAnswerEnum as AnswerEnum,
  QUESTIONNAIRE_ANSWER_DEFAULT_VALUE,
  QUESTIONNAIRE_TEST_DAY_THRESHOLD,
  QUESTIONNAIRE_TEMPERATURE_THRESHOLD,
  SortByType,
} from '../../constants'
import { SURVEYS } from './operations'

const DEFAULT_PAGE_LENGTH = 5
const DEFAULT_PAGE = 1

export interface QuestionnaireSummaryType {
  isTemperatureInTolerance: boolean
  hasOnlyNegativeAnswers: boolean
  hasTraveled: boolean
  hasPositiveTestWithinLastTenDays: boolean
}

interface Props {
  userId: string
  pageLength?: number
  page?: number
  sortDirection?: SortDirectionEnum
  sortBy?: SortByType
  skip?: boolean
  onlyMostRecentSurvey?: boolean
}

export const useQuestionnaire = ({
  userId,
  onlyMostRecentSurvey = false,
  pageLength = DEFAULT_PAGE_LENGTH,
  page = DEFAULT_PAGE,
  sortDirection = SortDirectionEnum.DESC,
  sortBy = SortByType.CREATED_AT,
  skip = false,
}: Props) =>
  useQuery<QuestionnaireSurveys, QuestionnaireSurveysVariables>(SURVEYS, {
    variables: {
      filterData: {
        types: [SurveyType.Questionnaire],
        userIds: [userId],
      },
      pagination: {
        from: onlyMostRecentSurvey ? 0 : pageLength * (page - 1),
        length: onlyMostRecentSurvey ? 1 : pageLength,
        sortBy,
        direction: sortDirection,
      },
    },
    skip: !userId || skip,
  })

type SurveyResponsesType = Array<{ key: string; value: string }>

export const getQuestionnaireResponsesSummary = (
  surveyResponses: SurveyResponsesType
): QuestionnaireSummaryType => {
  const getAnswerByQuestionKey = (key: QuestionKeys) =>
    surveyResponses.find(({ key: questionKey }) => questionKey === key)?.value

  const temperature = getAnswerByQuestionKey(QuestionKeys.TEMPERATURE_IN_FAHRENHEIT)
  const travelAnswer = getAnswerByQuestionKey(QuestionKeys.HAS_TRAVELLED)
  const lastPositiveResultDateAnswer = getAnswerByQuestionKey(
    QuestionKeys.DATE_OF_FIRST_POSITIVE_RESULT
  )

  const allSymptoms = Object.keys(Symptom).filter((symptom) => symptom !== Symptom.NoSymptoms)
  const relevantAnswers = [...allSymptoms, QuestionKeys.HAS_CLOSE_CONTANCT]
  const hasOnlyNegativeAnswers = surveyResponses
    .filter(({ key }) => relevantAnswers.includes(key))
    .every(({ value }) => [QUESTIONNAIRE_ANSWER_DEFAULT_VALUE, AnswerEnum.NO].includes(value))

  const hasPositiveTestDateAnswerWithinLastTenDays =
    !!lastPositiveResultDateAnswer &&
    differenceInCalendarDays(Date.now(), new Date(lastPositiveResultDateAnswer)) <=
      QUESTIONNAIRE_TEST_DAY_THRESHOLD

  return {
    isTemperatureInTolerance: Number(temperature) <= QUESTIONNAIRE_TEMPERATURE_THRESHOLD,
    hasTraveled: travelAnswer === AnswerEnum.YES,
    hasOnlyNegativeAnswers,
    hasPositiveTestWithinLastTenDays: hasPositiveTestDateAnswerWithinLastTenDays,
  }
}
